import { Component, Injectable, OnInit, ViewChild } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import "moment-duration-format";
import * as moment from "moment-timezone";
import "rxjs/add/observable/forkJoin";
import "rxjs/add/operator/switchMap";
import { AlertService } from "../../../shared/alert/services/alert.service";
import { AuthService } from "../../../authentication/services/auth.service";
import { ScheduledType } from "../../../shared/models/ScheduledContent";
import { ScheduleService } from "../../../shared/services/schedule.service";
import { SideBarService } from "../../../shared/services/sideBarService";
import { ScheduledGlobalAlert } from "../../models/globalalert";
import { GlobalAlertMainComponent } from "./main.component";
import { mapToCountryTagModelArray } from "../../../brokers/components/scripts/broker-model-utils";
import { CountryService } from "../../../brokers/services/country.service";
import { ButtonToggleGroupOption } from "../../../shared/models/buttonToggleGroupOption";
import { GealocalicationAction } from "../../models/globalalert";
import { concatMap, map, tap } from "rxjs/operators";
import { Observable } from "rxjs/Observable";
import { of } from "rxjs/observable/of";

@Component({
  selector: "app-globalalert-scheduled-create-edit",
  templateUrl: "../templates/scheduledCreateEdit.component.html",
  styleUrls: ["../styles/scheduledCreateEdit.component.scss"],
})
@Injectable()
export class GlobalAlertScheduledCreateEditComponent
  extends GlobalAlertMainComponent
  implements OnInit
{
  public globalAlert: ScheduledGlobalAlert;
  public dateUtc: moment.Moment;

  public selectedCountries: any[] = [];
  public autocompleteCountries: any[];
  public countrySelectorPlaceholder = "";
  public defaultGeolocationAction = GealocalicationAction.NoAction;
  public geolocationAction = this.defaultGeolocationAction;
  public isCountrySelectorDisabled =
    this.geolocationAction === GealocalicationAction.NoAction;
  public geolocationButtonToggleOptions: ButtonToggleGroupOption<GealocalicationAction>[] =
    [
      {
        label: "No Action",
        value: GealocalicationAction.NoAction,
        customStyles: { backgroundColor: "#b0bec5", color: "white" },
      },
      {
        label: "Ban",
        value: GealocalicationAction.Ban,
        customStyles: { backgroundColor: "#e57373", color: "white" },
      },
      {
        label: "Whitelist",
        value: GealocalicationAction.Whitelist,
        customStyles: { backgroundColor: "#66bb6a", color: "white" },
      },
    ];

  public validationError: {
    message: string;
    show: boolean;
    type: string;
  } = {
    message: "",
    show: false,
    type: "",
  };

  private scheduledGlobalAlertId?: string;

  constructor(
    private alertService: AlertService,
    private router: Router,
    private scheduleService: ScheduleService,
    private countryService: CountryService,
    private authService: AuthService,
    sideBarService: SideBarService
  ) {
    super(sideBarService);
    this.globalAlert = this.createEmptyGlobalAlert();
    this.dateUtc = moment.utc();
  }

  ngOnInit() {
    super.ngOnInit();
    this.initModel();
  }

  initModel() {
    this.getUrlParams();
    this.getScheduledGlobalAlert();
    this.initializeModelsAndDependencies();
  }

  getUrlParams() {
    const scheduledGlobalAlertId = window.location.hash.split("/")[4];
    this.scheduledGlobalAlertId = scheduledGlobalAlertId;
  }

  getAutocompleteCountries(): Observable<any[]> {
    return this.countryService.GetCountries().pipe(
      map((countries) => mapToCountryTagModelArray(countries)),
      tap((autocompleteCountries) => {
        this.autocompleteCountries = autocompleteCountries;
      })
    );
  }

  getScheduledGlobalAlert(): Observable<ScheduledGlobalAlert | null> {
    if (!this.isEdition()) {
      return of(null);
    }

    return this.scheduleService
      .GetScheduledById(this.scheduledGlobalAlertId)
      .pipe(
        tap((globalAlert: ScheduledGlobalAlert) => {
          this.globalAlert = globalAlert;
          this.dateUtc = globalAlert.dateUtc;
        }),
        tap((globalAlert: ScheduledGlobalAlert) => {
          const hasGeolocation = !!globalAlert.content.Geolocation;
          if (hasGeolocation) {
            const action = globalAlert.content.Geolocation.Action;
            const countries = globalAlert.content.Geolocation.Countries;
            this.initGeolocationActionWith(action, countries);
          }
        })
      );
  }

  initializeModelsAndDependencies(): void {
    this.getAutocompleteCountries()
      .pipe(concatMap(() => this.getScheduledGlobalAlert()))
      .subscribe();
  }

  public async onSubmit(form: FormGroup) {
    this.alertService.removeAll();
    if (
      !this.validateFormData(form) ||
      !this.validateScheduledDate() ||
      !this.validateGeolocation()
    ) {
      return;
    }

    await this.saveScheduledGlobalAlert()
      .then(() => {
        this.validationError.show = false;
        this.router.navigateByUrl("/editorialtools/scheduled");
      })
      .catch(() => {
        return false;
      });
  }

  initGeolocationActionWith(
    action: GealocalicationAction,
    countries?: string[]
  ) {
    this.geolocationAction = action;
    this.defaultGeolocationAction = action;
    this.selectedCountries = countries
      ? countries.map((country) => this.mapToTagModel(country))
      : [];
    this.onGeolocationActionChange(action);
  }

  onGeolocationActionChange(value: GealocalicationAction) {
    if (value === GealocalicationAction.NoAction) {
      this.disableCountrySelector();
      this.setCountrySelectorPlaceholder("Disabled");
    } else if (value === GealocalicationAction.Ban) {
      this.enableCountrySelector();
      this.setCountrySelectorPlaceholder("+ Banned Country");
    } else if (value === GealocalicationAction.Whitelist) {
      this.enableCountrySelector();
      this.setCountrySelectorPlaceholder("+ Whitelisted Country");
    } else {
      console.error(
        `Casuistry not implemented for GealocalicationAction ${value}`
      );
    }
  }

  private async saveScheduledGlobalAlert() {
    if (this.isBanOrWhitelisted()) {
      this.globalAlert.content.Geolocation = {
        Action: this.geolocationAction,
        Countries: this.selectedCountries.map((sc) => sc.value),
      };
    }

    const scheduledGlobalAlert = {
      id: this.globalAlert.id || "",
      dateUtc: this.dateUtc,
      content: this.globalAlert.content,
      type: ScheduledType.GlobalAlertMessage,
    };

    if (!this.scheduledGlobalAlertId) {
      return this.scheduleService
        .CreateScheduled<ScheduledGlobalAlert>(scheduledGlobalAlert)
        .map((scheduled) => {
          if (scheduled) {
            const result = {
              id: scheduled.id,
              dateUtc: scheduled.dateUtc,
              content: scheduled.content,
            };
            this.alertService.add(
              "success",
              "Scheduled global alert created successfully."
            );
            return result;
          }
        })
        .toPromise();
    } else {
      return this.scheduleService
        .EditScheduled<ScheduledGlobalAlert>(scheduledGlobalAlert)
        .map((scheduled) => {
          const result = {
            id: scheduled.id,
            dateUtc: scheduled.dateUtc,
            content: scheduled.content,
          };

          this.alertService.add(
            "success",
            "Scheduled global alert edited successfully."
          );

          return result;
        })
        .toPromise();
    }
  }

  private createEmptyGlobalAlert() {
    const globalAlert: ScheduledGlobalAlert = {
      id: "",
      dateUtc: "",
      content: {
        Id: "",
        Title: "",
        Link: "",
        ImageUrl: "",
        ExpirationTimeInMinutes: null,
        Geolocation: undefined,
        Culture: this.authService.getActualLanguage().cultureNameISO,
      },
    };
    return globalAlert;
  }

  private mapToTagModel(country: string): any {
    return this.autocompleteCountries.find((c) => c.value === country);
  }

  private validateFormData(form: FormGroup): boolean {
    this.validationError.show = false;

    if (form.valid) {
      return true;
    }

    this.validationError.show = true;
    this.validationError.type = "danger";
    this.validationError.message =
      "There are some errors in the form. Check required fields.";

    return false;
  }

  private validateScheduledDate(): boolean {
    this.validationError.show = false;

    if (this.dateUtc.add(this.dateUtc.utcOffset(), "m").utc() > moment.utc()) {
      return true;
    }

    this.validationError.show = true;
    this.validationError.type = "danger";
    this.validationError.message = "The scheduled date must be a future date.";

    return false;
  }

  private validateGeolocation(): boolean {
    this.validationError.show = false;

    const isSelectedCountriesEmpty = this.selectedCountries.length <= 0;

    if (this.isBanOrWhitelisted() && isSelectedCountriesEmpty) {
      this.validationError.show = true;
      this.validationError.type = "danger";
      this.validationError.message = "At least one country must be selected";

      return false;
    }

    return true;
  }

  private isEdition() {
    return !!this.scheduledGlobalAlertId;
  }

  private isBanOrWhitelisted() {
    return (
      this.geolocationAction === GealocalicationAction.Ban ||
      this.geolocationAction === GealocalicationAction.Whitelist
    );
  }

  private disableCountrySelector() {
    this.isCountrySelectorDisabled = true;
  }

  private enableCountrySelector() {
    this.isCountrySelectorDisabled = false;
  }

  private setCountrySelectorPlaceholder(value: string) {
    this.countrySelectorPlaceholder = value;
  }
}



// WEBPACK FOOTER //
// ./src/app/editorialtools/components/scripts/scheduledCreateEdit.component.ts